// src/components/WhyChoose.js
import React, { useRef, useEffect, useState } from 'react';
import ShapeDivider from './ShapeDivider';

const WhyChoose = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const benefitsRef = useRef([]);
  const jbsSectionRef = useRef(null);
  const jbsHeadingRef = useRef(null);
  const jbsContentRef = useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' ? window.location.origin : '');
  
  const [content, setContent] = useState({
    title: 'Why Choose Three Leaf?',
    items: [
      "Comprehensive Ingredient Portfolio – Covering all major food sectors.",
      "Consistent Quality & Traceability – Rigorous testing and supply chain transparency.",
      "Custom Formulations – Tailored blends and solutions to meet unique needs.",
      "Global Network, Regional Expertise – Best global sourcing, optimized for GCC and Middle East markets.",
      "On-Time Deliveries – Efficient logistics to keep production uninterrupted.",
      "Dedicated Technical & Sales Support – From concept to application."
    ]
  });

  const [jbsContent, setJbsContent] = useState({
    title: 'Juice & Beverage Solution (Specialized Arm of Three Leaf)',
    description: 'At Juice & Beverage Solution, every drop matters.',
    body: 'We deliver a wide spectrum of fruit-based solutions — concentrates, purees, NFCs, IQFs, compounds, and emulsions — sourced from premium origins worldwide.',
    promise: 'Our promise: freshness, sustainability, and consistency for every formulation. From beverages and dairy to desserts and confectionery, we are your trusted partner for fruit innovation in the GCC and Middle East.',
    tagline: 'Juice & Beverage Solution — Excellence in every sip.'
  });

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
        }
      });
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (headingRef.current) observer.observe(headingRef.current);
    if (jbsSectionRef.current) observer.observe(jbsSectionRef.current);
    if (jbsHeadingRef.current) observer.observe(jbsHeadingRef.current);
    if (jbsContentRef.current) observer.observe(jbsContentRef.current);
    
    benefitsRef.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      if (headingRef.current) observer.unobserve(headingRef.current);
      if (jbsSectionRef.current) observer.unobserve(jbsSectionRef.current);
      if (jbsHeadingRef.current) observer.unobserve(jbsHeadingRef.current);
      if (jbsContentRef.current) observer.unobserve(jbsContentRef.current);
      
      benefitsRef.current.forEach(el => {
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(API_BASE + '/backend/api.php/content?slug=why_choose', { signal: controller.signal })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        if (data && data.item) {
          const jsonData = data.item.json || {};
          const itemsArr = Array.isArray(data.item.json)
            ? data.item.json
            : (Array.isArray(jsonData.items) ? jsonData.items : []);
          // Convert to simple array if items have title/description structure
          const simpleItems = itemsArr.map(item => 
            typeof item === 'string' ? item : item.title || item
          );
          setContent(prev => ({
            ...prev,
            title: data.item.title || prev.title,
            items: simpleItems.length > 0 ? simpleItems : prev.items,
          }));
        }
      })
      .catch(() => {})
      .finally(() => controller.abort());
  }, [API_BASE]);

  // You could also fetch JBS content from API if needed
  useEffect(() => {
    const controller = new AbortController();
    fetch(API_BASE + '/backend/api.php/content?slug=juice_beverage_solution', { signal: controller.signal })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        if (data && data.item) {
          const jsonData = data.item.json || {};
          setJbsContent(prev => ({
            ...prev,
            title: data.item.title || prev.title,
            description: jsonData.description || prev.description,
            body: data.item.body || prev.body,
            promise: jsonData.promise || prev.promise,
            tagline: jsonData.tagline || prev.tagline
          }));
        }
      })
      .catch(() => {})
      .finally(() => controller.abort());
  }, []);

  return (
    <>
      <section id="why-choose" className="section why-choose-section" ref={sectionRef}>
        <div className="container">
          <h2 className="section-title animate-on-scroll slide-in-left" ref={headingRef}>{content.title}</h2>
          
          <div className="benefits-grid">
            {content.items.map((benefit, index) => {
              // Split the benefit into main text and description
              const parts = benefit.split(' – ');
              const mainText = parts[0] || benefit;
              const description = parts[1] || '';
              
              return (
                <div 
                  key={index}
                  className={`benefit-item animate-on-scroll fade-in delay-${index % 3}`}
                  ref={el => benefitsRef.current[index] = el}
                >
                  <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path fill="currentColor" d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z" />
                    </svg>
                  </div>
                  <div className="benefit-content">
                    <span className="benefit-main">{mainText}</span>
                    {description && <span className="benefit-description"> – {description}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <ShapeDivider />
      </section>

      {/* Juice & Beverage Solution Section */}
      <section id="juice-beverage-solution" className="section jbs-section" ref={jbsSectionRef}>
        <div className="container">
          <h2 className="section-title animate-on-scroll slide-in-left" ref={jbsHeadingRef}>
            {jbsContent.title}
          </h2>
          
          <div className="jbs-content animate-on-scroll fade-in" ref={jbsContentRef}>
            <p className="jbs-description">
              {jbsContent.description}
            </p>
            
            <p className="jbs-body">
              {jbsContent.body}
            </p>
            
            <p className="jbs-promise">
              {jbsContent.promise}
            </p>
            
            <p className="jbs-tagline">
              {jbsContent.tagline}
            </p>
          </div>
        </div>
        <ShapeDivider />
      </section>
    </>
  );
};

export default WhyChoose;